import { Checkbox, Flex, FormControl, FormLabel, Input, Select, Text } from '@chakra-ui/react';

import { RecurrenceFormState } from 'app/utils/campaigns/recurrence';

interface RecurrenceFieldsProps {
  form: RecurrenceFormState;
  setField: <K extends keyof RecurrenceFormState>(key: K, value: RecurrenceFormState[K]) => void;
  error?: string;
  /** The dates this repeat produces, shown beneath it so the rule is read as days rather than as words. */
  forecast?: React.ReactNode;
}

/**
 * How often a campaign runs again.
 * <p>
 * A successor is created as its predecessor opens, so this is the difference between an award that runs once
 * and one that keeps running. How often, and when it stops — which day it lands on is already settled by the
 * schedule above, and every later run keeps it.
 */
function RecurrenceFields({
  form,
  setField,
  error = undefined,
  forecast = undefined,
}: RecurrenceFieldsProps) {
  return (
    <Flex flexDir="column" gap="16px">
      <Checkbox
        isChecked={form.recurring}
        onChange={(event) => setField('recurring', event.target.checked)}
        data-testid="recurring-checkbox"
      >
        This award runs again on a schedule
      </Checkbox>

      {form.recurring && (
        <Flex flexDir="column" gap="16px" pl="26px" data-testid="recurrence-fields">
          <Flex gap="12px" flexWrap="wrap" align="flex-end">
            <FormControl w="150px">
              <FormLabel>Repeat every</FormLabel>
              <Input
                type="number"
                min={1}
                value={form.interval}
                onChange={(event) => setField('interval', event.target.value)}
                data-testid="recurrence-interval"
              />
            </FormControl>
            <FormControl w="170px">
              <FormLabel>Period</FormLabel>
              <Select
                value={form.frequency}
                onChange={(event) =>
                  setField('frequency', event.target.value as RecurrenceFormState['frequency'])
                }
                data-testid="recurrence-frequency"
              >
                <option value="WEEKLY">weeks</option>
                <option value="MONTHLY">months</option>
                <option value="YEARLY">years</option>
              </Select>
            </FormControl>
          </Flex>
          <Text textStyle="extraSmallRegular" color="brand.darkGray">
            Every run lands on the same day as the one above it — a campaign opening on a Monday
            keeps opening on a Monday.
          </Text>

          <FormControl>
            <FormLabel>Stops</FormLabel>
            <Flex gap="12px" flexWrap="wrap" align="center">
              <Select
                w="200px"
                value={form.endsType}
                onChange={(event) =>
                  setField('endsType', event.target.value as RecurrenceFormState['endsType'])
                }
                data-testid="recurrence-ends"
              >
                <option value="NEVER">Never</option>
                <option value="ON_DATE">On a date</option>
                <option value="AFTER_COUNT">After a number of runs</option>
              </Select>
              {form.endsType === 'ON_DATE' && (
                <Input
                  type="date"
                  w="200px"
                  value={form.endsOnDate}
                  onChange={(event) => setField('endsOnDate', event.target.value)}
                  data-testid="recurrence-ends-on"
                />
              )}
              {form.endsType === 'AFTER_COUNT' && (
                <Input
                  type="number"
                  min={1}
                  w="120px"
                  value={form.endsAfterCount}
                  onChange={(event) => setField('endsAfterCount', event.target.value)}
                  data-testid="recurrence-ends-after"
                />
              )}
            </Flex>
          </FormControl>

          {error && (
            <Text textStyle="extraSmallRegular" color="text.error" data-testid="recurrence-error">
              {error}
            </Text>
          )}

          {!error && forecast}
        </Flex>
      )}
    </Flex>
  );
}

export default RecurrenceFields;
